import React, { createContext, useContext, useEffect, useState, useCallback, useRef } from "react";
import { api, formatErr, setAuthToken } from "@/lib/api";
import { logError } from "@/lib/log";
import { getDeviceToken, rememberLoginDevice } from "@/lib/trustedDeviceStorage";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const loginEmail = useRef("");
  const identityVersion = useRef(0);

  const checkAuth = useCallback(async () => {
    const version = ++identityVersion.current;
    try {
      const { data } = await api.get("/auth/me");
      if (version === identityVersion.current) setUser(data);
    } catch {
      if (version === identityVersion.current) setUser(null);
    } finally {
      if (version === identityVersion.current) setLoading(false);
    }
  }, []);

  useEffect(() => {
    // If we're returning from Emergent Google OAuth (session_id in URL hash),
    // skip the initial /auth/me probe — AuthCallback will run the exchange
    // and then refresh the user object once the cookie is set.
    if (typeof window !== "undefined" && window.location.hash?.includes("session_id=")) {
      setLoading(false);
      return;
    }
    checkAuth();
    return () => { identityVersion.current += 1; };
  }, [checkAuth]);

  const login = async (email, password) => {
    loginEmail.current = email;
    try {
      const { data } = await api.post("/auth/login", {
        email, password,
        device_token: getDeviceToken("staff", email),
      });
      if (data.challenge_required) {
        // Unrecognized device — a code was emailed; complete via verifyLoginCode
        return { ok: false, challenge: data };
      }
      rememberLoginDevice("staff", email, data);
      identityVersion.current += 1;
      setAuthToken(data.token);
      setUser(data.user);
      setLoading(false);
      return { ok: true, user: data.user };
    } catch (e) {
      return { ok: false, error: formatErr(e.response?.data?.detail) || e.message };
    }
  };

  const verifyLoginCode = async (challengeId, code) => {
    try {
      const { data } = await api.post("/auth/verify-login-code", {
        challenge_id: challengeId, code, device_token: getDeviceToken("staff", loginEmail.current),
      });
      rememberLoginDevice("staff", loginEmail.current, data);
      identityVersion.current += 1;
      setAuthToken(data.token);
      setUser(data.user);
      setLoading(false);
      return { ok: true, user: data.user };
    } catch (e) {
      return { ok: false, error: formatErr(e.response?.data?.detail) || e.message };
    }
  };

  const register = async (payload) => {
    try {
      const { data } = await api.post("/auth/register", payload);
      identityVersion.current += 1;
      setAuthToken(data.token);
      setUser(data.user);
      setLoading(false);
      return { ok: true, user: data.user };
    } catch (e) {
      return { ok: false, error: formatErr(e.response?.data?.detail) || e.message };
    }
  };

  const logout = async () => {
    // Neither an older probe nor one started during sign-out may restore it.
    identityVersion.current += 1;
    try { await api.post("/auth/logout"); } catch (e) { logError("Logout API call failed:", e); }
    identityVersion.current += 1;
    setAuthToken(null);
    setUser(null);
    setLoading(false);
  };

  return (
    <AuthContext.Provider value={{ user, loading, login, verifyLoginCode, register, logout, refresh: checkAuth }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
